import { useMemo, useEffect, useRef, useState } from "react";
import { Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import type { DeliveredAttachmentDownloadPayload, DeliveredMessages } from "../API";
import type { DeliveredMessageListItem } from "../types/message";
import { convertUtcToLocal, logToConsole } from "../utilities/utilities";
import { useUser } from "../contexts/UserContext";
import MessageViewShell, { MessageViewAttachment } from "./MessageViewShell";
import ConfirmationModal from "./ui/ConfirmationModal";
import {
  createDeliveredMessageActivityLog,
  fetchDeliveredMessageById,
  markDeliveredMessageAsRead,
  requestDeliveredMessageAttachmentDownloads,
} from "../graphql/fetchers";
import { useDeliveredMessageAttachments } from "../graphql/hooks";
import { queryKeys } from "../graphql/queryKeys";
import {
  printMessageDocument,
  triggerSequentialDownloads,
} from "../utilities/messageViewActions";
import BlockingPageLoader from "./ui/BlockingPageLoader";

type DownloadState = {
  successfulIds: Set<string>;
  failedIds: Set<string>;
  allSucceeded: boolean;
  hasAttempted: boolean;
};

const DOWNLOAD_COOLDOWN_MS = 2500;
const DOWNLOAD_CLICK_DELAY_MS = 250;

function createInitialDownloadState(): DownloadState {
  return {
    successfulIds: new Set<string>(),
    failedIds: new Set<string>(),
    allSucceeded: false,
    hasAttempted: false,
  };
}

function isDetailedDeliveredMessage(
  message: DeliveredMessageListItem | DeliveredMessages | null,
): message is DeliveredMessages {
  return !!message && "body" in message && typeof message.body === "string";
}

function InheritedMessageView() {
  const { isAuthenticated } = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation("common");
  const location = useLocation();
  const { message: routeMessageId } = useParams<{ message?: string }>();
  const inheritedMsg =
    (location.state as DeliveredMessageListItem | DeliveredMessages | null) ||
    null;
  const deliveredMessageId = (routeMessageId ?? inheritedMsg?.id ?? "").trim();
  const routeDetailMessage =
    inheritedMsg && isDetailedDeliveredMessage(inheritedMsg)
      ? inheritedMsg
      : null;
  const [detailMessage, setDetailMessage] = useState<DeliveredMessages | null>(
    routeDetailMessage,
  );
  const [detailFetchCompleted, setDetailFetchCompleted] = useState(
    !!routeDetailMessage || !deliveredMessageId,
  );
  const [actionsBusy, setActionsBusy] = useState(false);
  const [isDownloadCooldownActive, setIsDownloadCooldownActive] =
    useState(false);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionWarning, setActionWarning] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [downloadState, setDownloadState] = useState<DownloadState>(
    createInitialDownloadState,
  );
  const [isRedownloadConfirmOpen, setIsRedownloadConfirmOpen] =
    useState(false);
  const markReadRequestedRef = useRef<string | null>(null);
  const cooldownTimeoutRef = useRef<number | null>(null);

  const attQ = useDeliveredMessageAttachments(
    deliveredMessageId,
    isAuthenticated && !!deliveredMessageId,
  );

  const displayedMessage = detailMessage ?? inheritedMsg;
  const isDetailMessagePending =
    !!deliveredMessageId && !detailMessage && !detailFetchCompleted;
  const recipientEmail = (
    detailMessage?.recipientEmail ??
    inheritedMsg?.recipientEmail ??
    ""
  )
    .trim()
    .toLowerCase();

  const lastEditedLabel = useMemo(() => {
    const ts: any =
      (displayedMessage as any)?.updatedAt ??
      (displayedMessage as any)?.createdAt ??
      null;
    if (!ts) return null;
    try {
      return convertUtcToLocal(ts, "ShowDayLong");
    } catch {
      return ts;
    }
  }, [displayedMessage]);

  const deliveryValue = useMemo(() => {
    const ts = displayedMessage?.deliveredAt || null;
    const local = convertUtcToLocal(ts, "ShowDayLong");
    return local || t("common.delivered");
  }, [displayedMessage, t]);

  const attachments = useMemo<MessageViewAttachment[]>(() => {
    const rows: any[] = (attQ.data ?? []) as any[];
    return rows.map((a) => ({
      id: a.id,
      fileName: a.fileName,
      fileType: a.fileType ?? null,
      fileSize: a.fileSize ?? null,
      s3Key: a.s3Key,
    }));
  }, [attQ.data]);

  const hasAttachments = attachments.length > 0;
  const actionsDisabled = actionsBusy || isDownloadCooldownActive;

  const downloadButtonLabel = useMemo(() => {
    if (downloadState.allSucceeded) {
      return t("inheritedMessage.actionLabel.downloaded");
    }

    if (downloadState.hasAttempted && downloadState.failedIds.size > 0) {
      return t("inheritedMessage.actionLabel.retryFailedDownload", {
        count: downloadState.failedIds.size,
      });
    }

    return t("inheritedMessage.actionLabel.downloadAttachments");
  }, [
    downloadState.allSucceeded,
    downloadState.failedIds.size,
    downloadState.hasAttempted,
    t,
  ]);

  function clearActionFeedback() {
    setActionMessage(null);
    setActionWarning(null);
    setActionError(null);
  }

  function clearDownloadCooldown() {
    if (cooldownTimeoutRef.current !== null) {
      window.clearTimeout(cooldownTimeoutRef.current);
      cooldownTimeoutRef.current = null;
    }
    setIsDownloadCooldownActive(false);
  }

  function startDownloadCooldown() {
    clearDownloadCooldown();
    setIsDownloadCooldownActive(true);
    cooldownTimeoutRef.current = window.setTimeout(() => {
      setIsDownloadCooldownActive(false);
      cooldownTimeoutRef.current = null;
    }, DOWNLOAD_COOLDOWN_MS);
  }

  function updateDownloadStateFromResponse(
    requestedIds: string[],
    result: DeliveredAttachmentDownloadPayload,
  ) {
    const nextSuccessfulIds = new Set(result.items.map((item) => item.id));
    const nextFailedIds = new Set(
      result.failures.length > 0
        ? result.failures.map((failure) => failure.id)
        : result.items.length === 0
          ? requestedIds
          : [],
    );

    setDownloadState((current) => {
      const successfulIds = new Set(current.successfulIds);
      const failedIds = new Set(current.failedIds);

      for (const id of requestedIds) {
        successfulIds.delete(id);
        failedIds.delete(id);
      }

      nextSuccessfulIds.forEach((id) => {
        successfulIds.add(id);
      });

      nextFailedIds.forEach((id) => {
        failedIds.add(id);
      });

      const allSucceeded =
        attachments.length > 0 &&
        attachments.every((attachment) => successfulIds.has(attachment.id));

      return {
        successfulIds,
        failedIds,
        allSucceeded,
        hasAttempted: true,
      };
    });
  }

  async function runAttachmentDownload(requestedIds: string[]) {
    if (!deliveredMessageId || requestedIds.length === 0) {
      return;
    }

    setActionsBusy(true);
    clearActionFeedback();

    try {
      const result = await requestDeliveredMessageAttachmentDownloads(
        deliveredMessageId,
        requestedIds,
      );

      if (result.items.length > 0) {
        await triggerSequentialDownloads(
          result.items.map((item) => ({
            downloadUrl: item.downloadUrl,
            fileName: item.fileName,
          })),
          DOWNLOAD_CLICK_DELAY_MS,
        );
      }

      updateDownloadStateFromResponse(requestedIds, result);

      const downloadedCount = result.items.length;
      const failedCount = result.failures.length;

      if (downloadedCount > 0 && failedCount === 0) {
        setActionMessage(t("inheritedMessage.onScreenMessage.downloadSuccess"));
      } else if (downloadedCount > 0 && failedCount > 0) {
        setActionWarning(
          t("inheritedMessage.onScreenMessage.downloadPartial", {
            downloadedCount,
            failedCount,
          }),
        );
      } else {
        setActionError(
          result.message ||
            t("inheritedMessage.onScreenMessage.downloadFailed"),
        );
      }
    } catch (error) {
      logToConsole("error", "Error requesting attachment downloads:", error);

      setDownloadState((current) => {
        const failedIds = new Set(current.failedIds);
        for (const id of requestedIds) {
          failedIds.add(id);
        }

        return {
          successfulIds: new Set(current.successfulIds),
          failedIds,
          allSucceeded: false,
          hasAttempted: true,
        };
      });
      setActionError(t("inheritedMessage.onScreenMessage.downloadFailed"));
    } finally {
      setActionsBusy(false);
      startDownloadCooldown();
    }
  }

  function getRequestedAttachmentIds(forceAll = false) {
    if (!downloadState.hasAttempted || forceAll || downloadState.failedIds.size === 0) {
      return attachments.map((attachment) => attachment.id);
    }

    return Array.from(downloadState.failedIds);
  }

  async function handlePrintMessage() {
    if (actionsBusy || !detailMessage?.id) {
      return;
    }

    setActionsBusy(true);
    clearActionFeedback();

    try {
      await createDeliveredMessageActivityLog({
        actionType: "RECIPIENT_PRINT_REQUESTED",
        entityId: detailMessage.id,
        originalMessageId: detailMessage.originalMessageId ?? null,
        deliveredMessageId: detailMessage.id,
        recipientId: detailMessage.recipientId ?? null,
        functionName: "InheritedMessageView",
        details: {
          requestSource: "message_view",
          viewType: "inherited",
          renderMode: "html",
          deliveredMessageId: detailMessage.id,
          originalMessageId: detailMessage.originalMessageId ?? null,
          recipientId: detailMessage.recipientId ?? null,
          hasAttachments,
          attachmentCount: attachments.length,
        },
      });
    } catch (error) {
      logToConsole("error", "Error logging inherited message print:", error);
      setActionError(t("inheritedMessage.onScreenMessage.activityLogFailed"));
      setActionsBusy(false);
      return;
    }

    try {
      await printMessageDocument({
        title:
          displayedMessage?.title ??
          t("message.onScreenMessage.untitledMessage"),
        bodyHtml: detailMessage.body ?? "",
      });
    } catch (error) {
      logToConsole("error", "Error printing inherited message:", error);
      setActionError(t("inheritedMessage.onScreenMessage.printFailed"));
    } finally {
      setActionsBusy(false);
    }
  }

  function handleDownloadAttachments() {
    if (actionsBusy || isDownloadCooldownActive || !hasAttachments) {
      return;
    }

    if (downloadState.allSucceeded) {
      setIsRedownloadConfirmOpen(true);
      return;
    }

    void runAttachmentDownload(getRequestedAttachmentIds());
  }

  function handleConfirmRedownload() {
    setIsRedownloadConfirmOpen(false);
    void runAttachmentDownload(getRequestedAttachmentIds(true));
  }

  useEffect(() => {
    return () => {
      clearDownloadCooldown();
    };
  }, []);

  useEffect(() => {
    clearActionFeedback();
    setDownloadState(createInitialDownloadState());
    setIsRedownloadConfirmOpen(false);
    clearDownloadCooldown();
  }, [deliveredMessageId]);

  useEffect(() => {
    let mounted = true;
    markReadRequestedRef.current = null;

    if (routeDetailMessage?.id === deliveredMessageId) {
      setDetailMessage(routeDetailMessage);
      setDetailFetchCompleted(true);
      return () => {
        mounted = false;
      };
    }

    setDetailFetchCompleted(false);
    setDetailMessage(null);

    if (deliveredMessageId) {
      fetchDeliveredMessageById(deliveredMessageId)
        .then((message) => {
          if (mounted) {
            setDetailMessage(message);
            setDetailFetchCompleted(true);
          }
        })
        .catch((error) => {
          if (mounted) {
            setDetailFetchCompleted(true);
            logToConsole(
              "log",
              "Error fetching delivered message detail:",
              error,
            );
          }
        });
    } else {
      setDetailFetchCompleted(true);
    }

    return () => {
      mounted = false;
    };
  }, [deliveredMessageId, routeDetailMessage]);

  useEffect(() => {
    if (!isAuthenticated || !detailMessage?.id || !recipientEmail) return;
    if (detailMessage.messageRead !== false) return;
    if (markReadRequestedRef.current === detailMessage.id) return;

    markReadRequestedRef.current = detailMessage.id;
    let cancelled = false;

    void (async () => {
      try {
        const markedRead = await markDeliveredMessageAsRead(detailMessage.id);
        if (cancelled) return;
        if (!markedRead) {
          markReadRequestedRef.current = null;
          return;
        }

        const { body: _body, ...deliveredMessageListItem } = markedRead;

        queryClient.setQueryData<DeliveredMessageListItem[] | undefined>(
          queryKeys.deliveredMessages(recipientEmail),
          (current) => {
            if (!current) return current;

            return current.map((message) =>
              message.id === detailMessage.id
                ? { ...message, ...deliveredMessageListItem }
                : message,
            );
          },
        );

        setDetailMessage(markedRead);
      } catch (error) {
        if (!cancelled) {
          markReadRequestedRef.current = null;
          logToConsole(
            "log",
            "Error marking delivered message as read:",
            error,
          );
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [detailMessage, isAuthenticated, queryClient, recipientEmail]);

  if (!deliveredMessageId) {
    return <Navigate to="/Panel?selectedTab=inheritedMessages" replace />;
  }

  if (isDetailMessagePending) {
    return <BlockingPageLoader message={t("message.loadingData")} />;
  }

  if (!displayedMessage && detailFetchCompleted) {
    return <Navigate to="/Panel?selectedTab=inheritedMessages" replace />;
  }

  return (
    <>
      <MessageViewShell
        variant="inherited"
        title={
          displayedMessage?.title ??
          t("message.onScreenMessage.untitledMessage")
        }
        lastEditedLabel={lastEditedLabel}
        deliveryLabel={t("inheritedMessage.tableColumn.deliveredOn")}
        deliveryValue={deliveryValue}
        bodyHtml={detailMessage?.body ?? ""}
        attachments={attachments}
        onBack={() => navigate(-1)}
        onEdit={() => {}}
        editDisabled={true}
        fromName={displayedMessage?.fromName}
        fromNameLabel={t("inheritedMessage.fromNameLabel")}
        fromEmail={displayedMessage?.fromEmail}
        onPrintMessage={handlePrintMessage}
        onDownloadAttachments={handleDownloadAttachments}
        showDownloadAttachments={hasAttachments}
        downloadAttachmentsLabel={downloadButtonLabel}
        actionsDisabled={actionsDisabled}
        actionMessage={actionMessage}
        actionWarning={actionWarning}
        actionError={actionError}
      />

      <ConfirmationModal
        isOpen={isRedownloadConfirmOpen}
        title={t("inheritedMessage.modal.redownloadTitle")}
        message={t("inheritedMessage.modal.redownloadMessage")}
        primaryLabel={t("inheritedMessage.actionLabel.downloadAgain")}
        secondaryLabel={t("common.cancel")}
        onPrimary={handleConfirmRedownload}
        onSecondary={() => setIsRedownloadConfirmOpen(false)}
      />
    </>
  );
}

export default InheritedMessageView;
